<template>
	<div
		class="shop-view"
		:class="{
			'pointer-events-none': isCameraActive,
		}"
	>
		<ModalWindow
			:heading="translate('ui.shop')"
			:isElevated="false"
			:skin="ModalWindowSkinTypes.PREMIUM"
		>
			<template #content>
				<Tabs :tabs="shopTabs" :selectedIndex="selectedTab">
					<template v-if="isEventActive" v-slot:tab1>
						<EventShop />
					</template>
					<template v-slot:[primaryTabSlotName]>
						<SpecialShop />
					</template>
					<template v-slot:[secondaryTabSlotName]>
						<RegularShop />
					</template>
				</Tabs>
			</template>
		</ModalWindow>
	</div>
</template>

<script lang="ts" setup>
import ModalWindow from "@/components/ModalWindow.vue";

import EventShop from "@/components/shop/EventShop.vue";
import RegularShop from "@/components/shop/RegularShop.vue";
import SpecialShop from "@/components/shop/SpecialShop.vue";
import Tabs from "@/components/Tabs.vue";
import { useEventStore } from "@/event";
import { OfferProvider } from "@/services/OfferProvider";
import useRuntimeStore from "@/store/useRuntimeStore";
import type { Tab } from "@/types/ui";
import { ModalWindowSkinTypes, ShopTab } from "@/types/ui";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();

const runtimeStore = useRuntimeStore();
const { isCameraActive } = storeToRefs(runtimeStore);

const eventStore = useEventStore();
const { isActive: isEventActive } = storeToRefs(eventStore);

const hasRegularShopNotification = computed(
	(): boolean => OfferProvider.freeRegularOfferAvailable,
);

const hasEventShopNotification = computed(
	(): boolean => OfferProvider.freeEventOfferAvailable,
);

const selectedTab = ref<number>(0);

const shopTabs = computed((): Tab[] => {
	if (isEventActive.value) {
		return [
			{
				name: "event.event",
				slug: ShopTab.EVENT,
				showEventIcon: true,
				hasNotification: hasEventShopNotification.value,
			},
			{ name: "ui.specials", slug: ShopTab.PREMIUM },
			{
				name: "ui.shop",
				slug: ShopTab.REGULAR,
				hasNotification: hasRegularShopNotification.value,
			},
		];
	}
	return [
		{ name: "ui.specials", slug: ShopTab.PREMIUM },
		{
			name: "ui.shop",
			slug: ShopTab.REGULAR,
			hasNotification: hasRegularShopNotification.value,
		},
	];
});

const primaryTabSlotName = computed((): string =>
	isEventActive.value ? "tab2" : "tab1",
);

const secondaryTabSlotName = computed((): string =>
	isEventActive.value ? "tab3" : "tab2",
);

// Target tab slug or fallback to regular shop if scrollTo param is present
const targetTabSlug = computed((): string => {
	const tabParam = route.params.tab as string;
	const scrollToParam = route.params.scrollTo as string;

	if (tabParam) return tabParam;
	if (scrollToParam) return ShopTab.REGULAR;

	return "";
});

// Watch route params and update selected tab index dynamically
watch(
	[targetTabSlug, shopTabs],
	([targetSlug]: [string, Tab[]]): void => {
		if (targetSlug) {
			const foundIndex = shopTabs.value.findIndex(
				(item: Tab): boolean => item.slug === targetSlug,
			);

			if (foundIndex !== -1) {
				selectedTab.value = foundIndex;
			}
		}
	},
	{ immediate: true },
);
</script>

<style lang="scss" scoped>
.shop {
	@include flex-col-start;
	gap: 0.75rem;
	padding: 0 0.25rem;

	&-view {
		&.not-visible {
			opacity: 0;
			pointer-events: none;
		}

		:deep(.modal-window) {
			box-shadow: inset 0 0 0 0.125rem rgb(255 255 255 / 40%);

			.modal-window__content {
				.scroll-fade-container {
					.tabs__list-item {
						&--active {
							background: linear-gradient(
								to top,
								rgba(98, 38, 120, 0.3),
								rgba(98, 38, 120, 0.5) 90%,
								rgba(177, 104, 204, 0.5)
							);
						}
					}
					&--tabs {
						background: linear-gradient(
							to top,
							rgba(28, 2, 37, 0.3) 0%,
							rgba(98, 38, 120, 0.3) 100%
						);
					}

					.scrollbar__content {
						padding: 0 0.125rem; // 0.125 >> modal-window color inset

						.shop {
							padding: 0.5rem 0.5rem 1.125rem;
							width: 100%;
						}
					}
				}
			}
		}
	}
}

:deep(.rectangle-box-decoration .golden-corner) {
	width: 1.5rem;
	height: 1.5rem;
}
</style>
